import {
  WSElementColorsType,
  WSIconName
} from "@wingspanhq/fe-component-library";
import type { planEnrollmentApiSpec } from "@wingspanhq/wingspan-api-types";
import React from "react";

export enum BenefitCategory {
  Health = "Health",
  Legal = "Legal",
  Wellness = "Wellness",
  Finances = "Finances",
  Other = "Other",
  Insurance = "Insurance"
}

/**
 * Runtime gate limiting a benefit's availability. Benefits without a gate are
 * always in the catalog.
 */
export enum BenefitGate {
  /** Requires the `meridio` feature flag. */
  MeridioFlag = "MeridioFlag",
  /** Requires the master-policy flow (feature flag or a master-policy payer). */
  MasterPolicy = "MasterPolicy",
  /** Axis partner catalog, shown on its own route. */
  AxisPartner = "AxisPartner"
}

/** One card in the `mainCards` grid on the detail page. */
export type BenefitCard = {
  icon?: WSIconName;
  /** First line renders as the card title when more than one line is given. */
  lines: string[];
};

/** One numbered step in the "How it works" list. */
export type BenefitInfoStep = {
  title: string;
  description?: string[];
};

export type BenefitFaq = {
  question: string;
  /** Plain paragraphs, or arbitrary content when the answer needs links or lists. */
  answer: string[] | React.ReactNode;
};

/**
 * A single benefit in the hardcoded catalog.
 *
 * Replaces the Webflow CMS `IWebflowBenefit` shape. Fields that the CMS
 * carried but nothing ever read (`featured`, `itemId`, `publishedOn`,
 * `editedOn`, `collectionId`, `isDraft`, `hidden`) are gone,
 * and the newline-delimited CMS strings are modelled as real arrays.
 */
export type Benefit = {
  // Identity and placement
  /** Unique key. Used as the detail-route param, and as the plan type for enrollable insurance. */
  slug: string;
  name: string;
  category: BenefitCategory;
  /** Ascending. Benefits without a priority sort last, then alphabetically by name. */
  orderPriority?: number;
  gate?: BenefitGate;

  // Eligibility
  /** Shown as an upsell rather than as included in the member's plan. */
  isAdditionalCoverage?: boolean;

  // List card
  atAGlance: string;
  logoUrl?: string;
  /** Rendered logo width in px. Defaults to 48. */
  logoWidth?: number;
  /** Fallback avatar icon, used when there is no logo. */
  icon?: WSIconName;
  iconColor?: WSElementColorsType;
  iconBackgroundColor?: WSElementColorsType;

  // Cost
  /** Suppresses the cost line on the card and the cost box on the detail page. */
  hideCost?: boolean;
  costDescription?: string;
  /** The cost is not covered by the membership. */
  costIsNotIncluded?: boolean;
  /** Compact cost label for insurance cards. */
  costShortDescription?: string;

  // Detail page — main column
  mainHeader: string;
  mainSubheader?: string;
  videoUrl?: string;
  mainDescription?: string | React.ReactNode;
  mainDescriptionList?: string[];
  mainCards?: BenefitCard[];
  mainInfoListHeader?: string;
  mainInfoListSteps?: BenefitInfoStep[];
  mainFaqsHeader?: string;
  mainFaqs?: BenefitFaq[];
  mainFootnote?: string;
  mainLearnMoreLink?: string;
  mainLearnMoreLinkText?: string;

  // Detail page — sidebar column
  sidebarHeader?: string;
  sidebarLogoUrl?: string;
  /** Alt/fallback text for the sidebar logo. */
  sidebarLogoPlaceholder?: string;
  /** Supports the `{{membershipId}}` and `{{groupId}}` placeholders. */
  sidebarUsageText?: string;
  sidebarHideMemberIds?: boolean;
  sidebarCtaLink?: string;
  sidebarCtaText?: string;
  /** Replaces the primary CTA button entirely. */
  sidebarCtaCustom?: React.ReactNode;
  sidebarContactDescription?: string;
  sidebarContactEmail?: string;
  sidebarContactPhone?: string;

  // Behaviour
  /** Groups the benefit under "Independent contractor insurance". */
  isAssurely?: boolean;
  /** Enrolls via an external link instead of the in-app flow. */
  customEnrollLink?: string;
  /** Plan type used to look up an active enrollment for the "Enrolled" pill. */
  enrolledPlanType?: planEnrollmentApiSpec.PlanEnrollment["planType"];
};
